import { useEffect, useState, useContext } from "react";
import PageWrapper from "../../components/PageWrapper";
import RoundedTable, {
    TABLE_CELL_TYPES,
} from "../../components/table-component/RoundedTable";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/CustomButton";
import SearchFilter from "../../components/SearchFilter";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import StatusBadge from "../../components/StatusBadge";
import Loader from "../../components/Loader";
import InformationBox from "../../components/InformationBox";
import ErrorBox from "../../components/ErrorBox";
import useLanguage from "../../utils/useLanguage";
import { getBugReports } from "../../services/bank/banksAnalyzeService";
import { UserAuth } from "../../utils/AuthContext";
import { Link } from "react-router-dom";

const BugReportsList = () => {
    const { t } = useTranslation("general");
    const { currentDirection } = useLanguage();
    const { userData } = useContext(UserAuth);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [pagination, setPagination] = useState({
        currentPage: 1,
        limit: 10,
        totalRecords: 0,
    });

    const [selectedStatus, setSelectedStatus] = useState({
        id: "",
        nameInAr: t("selectAll"),
    });

    const statusOptions = [
        { id: "", nameInAr: t("selectAll") },
        { id: "OPEN", nameInAr: t("open") },
        { id: "IN_PROGRESS", nameInAr: t("inProgress") },
        { id: "RESOLVED", nameInAr: t("resolved") },
        { id: "CLOSED", nameInAr: t("closed") },
    ];

    const fetchReports = async (page = 1) => {
        setLoading(true);
        setError("");
        try {
            const params = {
                page,
                limit: pagination.limit,
                status: selectedStatus.id || undefined,
            };

            const response = await getBugReports(params);

            if (response.status === 200) {
                const reports = response.data.reports || [];
                const formattedData = reports.map((report) => [
                    {
                        type: TABLE_CELL_TYPES.LINK,
                        value: report.createdAt ? new Date(report.createdAt).toLocaleDateString() : "-",
                        to: `/bug-reports/${report.id}`,
                        keepOnMobile: true,
                    },
                    {
                        type: TABLE_CELL_TYPES.TEXT,
                        value: report.user?.username || "-",
                        keepOnMobile: true,
                    },
                    {
                        type: TABLE_CELL_TYPES.TEXT,
                        value: report.organization?.name || "-",
                        keepOnMobile: true,
                    },
                    {
                        type: TABLE_CELL_TYPES.TEXT,
                        value: report.description ? (report.description.length > 50 ? report.description.substring(0, 50) + "..." : report.description) : "-",
                        keepOnMobile: true,
                    },
                    {
                        type: TABLE_CELL_TYPES.ELEMENT,
                        value: (
                            <StatusBadge
                                color={
                                    report.status === "OPEN" ? "red" :
                                        report.status === "IN_PROGRESS" ? "blue" :
                                            report.status === "RESOLVED" ? "green" : "gray"
                                }
                                title={
                                    report.status === "OPEN" ? t("open") :
                                        report.status === "IN_PROGRESS" ? t("inProgress") :
                                            report.status === "RESOLVED" ? t("resolved") :
                                                report.status === "CLOSED" ? t("closed") : report.status
                                }
                            />
                        ),
                        keepOnMobile: true,
                    },
                ]);

                setData(formattedData);
                setPagination(prev => ({
                    ...prev,
                    currentPage: response.data.currentPage || page,
                    totalPages: response.data.totalPages || 1,
                    totalRecords: response.data.totalReports || 0,
                }));
            }
        } catch (err) {
            console.error(err);
            setError(t("errorFetchingReports"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReports(pagination.currentPage);
    }, [pagination.currentPage, selectedStatus]);

    const tableTitles = [
        { value: "date" },
        { value: "creator" },
        { value: "organization" },
        { value: "description" },
        { value: "status" },
    ];

    return (
        <PageWrapper  title={t("bugReports")}>
            <div className="o-row o-row--gap-size_100 t-bold_bold u-margin--top_400">
                <span>{t("user")}:</span>
                <span>{`${localStorage.getItem("username")}`}</span>
            </div>

            <div className="c-search-bar u-margin--bottom_500 u-margin--top_400">
                <section className="c-search-bar-label-input">
                    <label>{t("status")}:</label>
                    <Dropdown
                        options={statusOptions}
                        selected={selectedStatus}
                        setSelected={(val) => {
                            setSelectedStatus(val);
                            setPagination(prev => ({ ...prev, currentPage: 1 }));
                        }}
                        thick
                        nameInAr={true}
                    />
                </section>
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}
            {loading && <Loader />}

            {!loading && !error && data.length > 0 && (
                <RoundedTable
                    titles={tableTitles}
                    rows={data}
                    pagination={pagination}
                    setPagination={setPagination}
                    mobileEffect={true}
                />
            )}

            {!loading && !error && data.length === 0 && (
                <InformationBox>
                    <p>{t("noResultFound")}</p>
                </InformationBox>
            )}
        </PageWrapper>
    );
};

export default BugReportsList;
